import { useEffect } from "react";
import {
  Divider,
  Group,
  NumberInput,
  Radio,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { qk } from "@/lib/apiHelpers";
import type {
  BodyType,
  BodyTypesResponse,
  Carrier,
  CarriersListResponse,
  Vehicle,
  VehicleKind,
} from "@/types/api";

export interface VehicleFormValues {
  kind: VehicleKind;
  // Tractor / single
  brand: string;
  license_plate: string;
  model: string;
  vin: string;
  year: number | "";
  // Trailer (autopoezd only)
  trailer_brand: string;
  trailer_license_plate: string;
  trailer_body_type_id: string;
  trailer_year: number | "";
  // Common optional
  capacity_kg: number | "";
  volume_m3: number | "";
  body_type_id: string; // for single
  carrier_id: string;
  notes: string;
}

export type VehicleFormApi = ReturnType<typeof useForm<VehicleFormValues>>;

export function VehicleForm({
  initial,
  onChange,
}: {
  initial?: Vehicle;
  onChange: (api: VehicleFormApi) => void;
}) {
  const form = useForm<VehicleFormValues>({
    initialValues: {
      kind: initial?.kind ?? "single",
      brand: initial?.brand ?? "",
      license_plate: initial?.license_plate ?? "",
      model: initial?.model ?? "",
      vin: initial?.vin ?? "",
      year: initial?.year ?? "",
      trailer_brand: initial?.trailer_brand ?? "",
      trailer_license_plate: initial?.trailer_license_plate ?? "",
      trailer_body_type_id: initial?.trailer_body_type_id ?? "",
      trailer_year: initial?.trailer_year ?? "",
      capacity_kg: initial?.capacity_kg ?? "",
      volume_m3: initial?.volume_m3 ?? "",
      body_type_id: initial?.body_type_id ?? "",
      carrier_id: initial?.carrier_id ?? "",
      notes: initial?.notes ?? "",
    },
    validate: {
      kind: (v) => (v ? null : "Обязательно"),
      brand: (v) => (v.trim() ? null : "Обязательно"),
      license_plate: (v) => (v.trim() ? null : "Обязательно"),
      trailer_brand: (v, vals) =>
        vals.kind === "autopoezd" && !v.trim() ? "Обязательно для автопоезда" : null,
      trailer_license_plate: (v, vals) =>
        vals.kind === "autopoezd" && !v.trim() ? "Обязательно для автопоезда" : null,
      trailer_body_type_id: (v, vals) =>
        vals.kind === "autopoezd" && !v ? "Обязательно для автопоезда" : null,
    },
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  // Без [] — Mantine useForm возвращает новый объект формы на ререндер.
  // Родитель должен хранить formApi в useRef, иначе видит только initial values.
  useEffect(() => {
    onChange(form);
  });

  const { data: bodyTypesData } = useQuery({
    queryKey: qk.bodyTypes(),
    queryFn: async () =>
      (await api.get<BodyTypesResponse>("/api/v1/refs/body-types")).data,
  });

  const { data: carriersData } = useQuery({
    queryKey: qk.carriers({ page: 1, page_size: 200 }),
    queryFn: async () =>
      (await api.get<CarriersListResponse>("/api/v1/carriers?page=1&page_size=200")).data,
  });

  const bodyTypeOptions = (bodyTypesData?.items ?? []).map((bt: BodyType) => ({
    value: bt.id,
    label: bt.name,
  }));

  const carrierOptions = (carriersData?.items ?? []).map((c: Carrier) => ({
    value: c.id,
    label: c.name,
  }));

  const isAutopoezd = form.values.kind === "autopoezd";
  // Год выпуска: максимум 4 цифры (= max текущий+1), при этом свободно
  // вводится/стирается. clampBehavior="strict" не даёт превысить max (т.е. не
  // даёт ввести 5-ю цифру / абсурдное число). ВАЖНО: НЕ ставить min — иначе
  // strict блокирует промежуточные значения при наборе и удалении (старый баг:
  // «1980 не стирается, ввести нельзя»).
  const maxYear = new Date().getFullYear() + 1;

  return (
    <Stack gap="md">
      {/* ---- Тип ---- */}
      <Radio.Group
        label="Тип транспортного средства"
        {...form.getInputProps("kind")}
      >
        <Group mt="xs">
          <Radio value="single" label="Одиночная" />
          <Radio value="autopoezd" label="Автопоезд (тягач + прицеп)" />
        </Group>
      </Radio.Group>

      <Divider />

      {/* ---- Тягач / одиночка ---- */}
      <Title order={5}>{isAutopoezd ? "Тягач" : "Транспортное средство"}</Title>
      <Group grow>
        <TextInput
          label="Марка"
          placeholder={isAutopoezd ? "DAF, MAN, RENAULT..." : "ГАЗ, Iveco..."}
          required
          {...form.getInputProps("brand")}
        />
        <TextInput
          label="Модель"
          placeholder={isAutopoezd ? "FT XF 950" : "Газель Next"}
          {...form.getInputProps("model")}
        />
      </Group>
      <Group grow>
        <TextInput
          label="Госномер тягача"
          required
          placeholder="HT/67"
          {...form.getInputProps("license_plate")}
        />
        <NumberInput
          label="Год выпуска"
          placeholder="2018"
          max={maxYear}
          clampBehavior="strict"
          allowDecimal={false}
          allowNegative={false}
          {...form.getInputProps("year")}
        />
      </Group>
      <TextInput
        label="VIN"
        placeholder="XLRTE47MS0E123456"
        {...form.getInputProps("vin")}
      />

      {/* ---- Прицеп (только для автопоезда) ---- */}
      {isAutopoezd && (
        <>
          <Divider />
          <Title order={5}>Прицеп</Title>
          <Group grow>
            <TextInput
              label="Марка прицепа"
              placeholder="Schmitz, Krone, Wielton..."
              required
              {...form.getInputProps("trailer_brand")}
            />
            <TextInput
              label="Госномер прицепа"
              required
              placeholder="HX/67"
              {...form.getInputProps("trailer_license_plate")}
            />
          </Group>
          <Group grow>
            <Select
              label="Тип кузова прицепа"
              placeholder="Выберите тип кузова"
              data={bodyTypeOptions}
              required
              searchable
              {...form.getInputProps("trailer_body_type_id")}
            />
            <NumberInput
              label="Год прицепа"
              placeholder="2019"
              max={maxYear}
              clampBehavior="strict"
              allowDecimal={false}
              allowNegative={false}
              {...form.getInputProps("trailer_year")}
            />
          </Group>
        </>
      )}

      {/* ---- Технические (опционально) ---- */}
      <Divider />
      <Title order={5}>Технические данные</Title>
      <Group grow>
        <NumberInput
          label="Грузоподъёмность (кг)"
          placeholder="20000"
          min={0}
          {...form.getInputProps("capacity_kg")}
        />
        <NumberInput
          label="Объём кузова (м³)"
          placeholder="86"
          min={0}
          decimalScale={2}
          {...form.getInputProps("volume_m3")}
        />
      </Group>

      {/* Тип кузова для одиночки */}
      {!isAutopoezd && (
        <Select
          label="Тип кузова"
          placeholder="Выберите тип кузова (опционально)"
          data={bodyTypeOptions}
          clearable
          searchable
          {...form.getInputProps("body_type_id")}
        />
      )}

      {/* ---- Перевозчик ---- */}
      <Divider />
      <Title order={5}>Перевозчик</Title>
      <Select
        label="Перевозчик"
        placeholder="Выберите перевозчика (опционально)"
        data={carrierOptions}
        clearable
        searchable
        {...form.getInputProps("carrier_id")}
      />

      {/* ---- Заметки ---- */}
      <Textarea
        label="Заметки"
        {...form.getInputProps("notes")}
        autosize
        minRows={2}
      />
    </Stack>
  );
}
